/**
 * Keep three.js from re-resolving shader programs every draw.
 *
 * three.js caches one program per material and re-checks it on every draw: when the same
 * material is drawn on objects that need different programs (skinned vs static vs instanced,
 * different morph-target counts) it re-derives the program parameters and the cache key (a big
 * string join) on each switch. Two cases hit us every frame:
 *  - the shadow pass draws every caster with one shared depth material, alternating between
 *    skinned heroes/mobs, static weapons and instanced dungeon props;
 *  - VRoid face primitives share one MToon material although only some have morph targets.
 * `stabilizePrograms` gives skinned / instanced / morphing casters their own depth material
 * (cached per kind + source material) and clones a surface material for each extra object kind
 * it is drawn on. Rendering is unchanged.
 */
import * as THREE from 'three';

/** What three.js keys a program on, per object (beyond the material itself). */
export function programSignature(o: THREE.Mesh): string {
  const g = o.geometry;
  const morph = g.morphAttributes['position']?.length ?? 0;
  const col = g.attributes['color'];
  return `${(o as THREE.SkinnedMesh).isSkinnedMesh ? 's' : ''}${(o as THREE.InstancedMesh).isInstancedMesh ? 'i' : ''}${(o as THREE.InstancedMesh).instanceColor ? 'c' : ''}${morph ? `m${morph}` : ''}${col && col.itemSize === 4 ? 'a' : ''}${g.attributes['tangent'] ? 't' : ''}`;
}

const depthCache = new Map<string, THREE.MeshDepthMaterial>();

function depthFor(sig: string, src: THREE.Material): THREE.MeshDepthMaterial {
  // alphaTest crossing 0 bumps the material version (program change), so it's part of the key.
  const key = `${sig}|${src.uuid}|${src.alphaTest > 0 ? 1 : 0}`;
  let m = depthCache.get(key);
  if (!m) {
    m = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking });
    depthCache.set(key, m);
  }
  return m;
}

/** Material uuid → the object signature it was first drawn with (and clones for other signatures). */
const surfaceOwner = new WeakMap<THREE.Material, string>();
const surfaceClones = new WeakMap<THREE.Material, Map<string, THREE.Material>>();

function surfaceFor(m: THREE.Material, sig: string): THREE.Material {
  const owner = surfaceOwner.get(m);
  if (owner === undefined) {
    surfaceOwner.set(m, sig);
    return m;
  }
  if (owner === sig) return m;
  let byKind = surfaceClones.get(m);
  if (!byKind) surfaceClones.set(m, (byKind = new Map()));
  let c = byKind.get(sig);
  if (!c) {
    c = m.clone();
    c.name = m.name;
    // onBeforeCompile hooks (toon / outline shaders) and their shared uniforms carry over.
    c.onBeforeCompile = m.onBeforeCompile;
    c.customProgramCacheKey = m.customProgramCacheKey;
    surfaceOwner.set(c, sig);
    byKind.set(sig, c);
  }
  return c;
}

export interface StabilizeOpts {
  /** Also split surface materials shared across object kinds (VRMs, whose materials are per instance). */
  surfaces?: boolean;
}

export function stabilizePrograms(root: THREE.Object3D, opts: StabilizeOpts = {}): void {
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const sig = programSignature(mesh);
    const first = (Array.isArray(mesh.material) ? mesh.material[0] : mesh.material) as THREE.Material | undefined;
    if (!first) return;
    // Plain static meshes keep three's shared depth material (they are then its only users).
    if (mesh.castShadow && sig !== '' && !mesh.customDepthMaterial) mesh.customDepthMaterial = depthFor(sig, first);
    if (opts.surfaces) {
      mesh.material = Array.isArray(mesh.material) ? mesh.material.map((m) => surfaceFor(m, sig)) : surfaceFor(mesh.material, sig);
    }
  });
}
